import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone' // dependent on utc plugin

// Extend dayjs with necessary plugins
dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

// Format the date for display on chart X-axis (Month Day)
export const formatDateForXAxis = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A'
    try {
        // Use dayjs for more robust parsing and timezone handling
        // Assume the date string might be in UTC or local time, parse accordingly
        const date = dayjs(dateStr).tz(dayjs.tz.guess()) // Guess user's timezone
        
        // Check if the date is valid after parsing
        if (!date.isValid()) {
            return dateStr // Return original string if parsing fails
        }

        // Format consistently as Month Day
        return date.format('MMMM D')
    } catch (e) {
        console.error("Error formatting date for X-axis:", e)
        return dateStr // Fallback to original string on error
    }
}

// Parses various date string formats and returns a formatted string (Month Day, Year or Year)
export const formatFlexibleDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A'

    const formats = [
        'YYYY-MM-DD',       // 2024-12-17
        'MMMM D, YYYY',     // August 1, 2024 // Keep MMMM for parsing
        'MMM D, YYYY',      // Aug 1, 2024   // Add MMM for parsing
        'YYYY',             // 2024
        'MMMM YYYY',        // February 2025 // Keep MMMM for parsing
        'MMM YYYY',         // Feb 2025    // Add MMM for parsing
    ]

    let date = null

    for (const format of formats) {
        const parsedDate = dayjs(dateStr, format, true) // Use strict parsing
        if (parsedDate.isValid()) {
            date = parsedDate
            break
        }
    }

    // If no specific format matched, try default parsing
    if (!date) {
        date = dayjs(dateStr)
    }


    if (!date || !date.isValid()) {
        console.warn(`Could not parse date string: ${dateStr}`)
        return dateStr // Return original string if parsing fails after all attempts
    }

    // Check if the original string was just a year
    if (/^\d{4}$/.test(dateStr.trim())) {
        return date.format('YYYY')
    }

    // Check if the original string was Month Year (using original MMMM/MMM for robustness)
    if (dayjs(dateStr, 'MMMM YYYY', true).isValid() || dayjs(dateStr, 'MMM YYYY', true).isValid()) {
        return date.format('MMM YYYY') // Output with MMM
    }

    // Default format: Month Day, Year
    return date.format('MMM D, YYYY') // Output with MMM
} 